import { Injectable, Injector } from "@angular/core";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import {
  ScenarioResults,
  ScenarioOptionWinner,
  AccountSymbolBalanceMap,
} from "@bitwarden/web-vault/app/models/types/scenario-group.types";
import { CalculationServiceAbstraction } from "@bitwarden/web-vault/app/services/DataCalculationService/calculation.service.abstraction";
import { ScenarioOption } from "@bitwarden/web-vault/app/services/DataCalculationService/scenarios/ScenarioOptions/scenario-option";
import { ScenarioOptionsFactory } from "@bitwarden/web-vault/app/services/DataCalculationService/scenarios/scenario-options-factory";
import { PreferenceService } from "@bitwarden/web-vault/app/services/DataService/preference/preference.service";
import { WebWorkerQueue } from "@bitwarden/web-vault/app/services/web-worker/WebWorkerQueue";

import { MainProgressBar } from "../../../services/progress-bar/main-progress-bar";
import { ScenarioGroupView } from "@bitwarden/web-vault/app/models/view/scenario-group/scenario-group.view";
import { ScenarioView } from "@bitwarden/web-vault/app/models/view/scenario/scenario.view";
import { BalanceByAccountsSymbols } from "@bitwarden/web-vault/app/models/types/balanceGroupingTypes";
import { BalanceCalculatedUtils } from "@bitwarden/web-vault/app/services/store/calculation-base/balances/balance.calculated.utils";

@Injectable({
  providedIn: "root",
})
export class ScenarioService implements CalculationServiceAbstraction {
  scenarioWinners: ScenarioResults[] = [];
  scenariosCompleted = 0;
  callback: (scenarioWinners: ScenarioResults[]) => Promise<void>;
  scenarios: ScenarioView[] = [];
  scenarioOptions: ScenarioOption[] = [];
  webWorkerQueue: WebWorkerQueue;
  completedPermutations = 0;
  totalPermutations = 0;
  preferenceService: PreferenceService;
  private scenarioStepIndex: {
    name: string;
    permutationLength: number;
    stepIndex: number;
    scenarioIndex: number;
  }[] = [];
  /** Bumped by every runScenarioGroup(). This service is a root singleton whose run state (callback,
   * winners, counters, options) is shared by every caller, so a run started while an older one is
   * still in flight (a new session's first refresh, after a logout/login or reloadFromVault()) resets
   * it from under the older run. The older run sees it's been superseded after each await and
   * stops, and its late completions are dropped, so only the newest run's results reach its own
   * callback. The superseded run's callback never fires, so its onSuperseded is called instead (its
   * caller is the session being replaced, which still has to wind its refresh down). That happens
   * when the newer run starts, as the older run's results may still be in the worker queue then,
   * long after runScenarioGroup() itself returned. */
  private runToken = 0;
  private onCurrentRunSuperseded?: () => void;

  constructor(
    private injector: Injector,
    private mainProgressBar: MainProgressBar,
    private i18nService: I18nService,
  ) {
    this.webWorkerQueue = WebWorkerQueue.getInstance();
    this.preferenceService = this.injector.get(PreferenceService);
  }

  async runScenarioGroup(
    scenarioGroup: ScenarioGroupView,
    scenarios: ScenarioView[],
    webWorkerQueue: WebWorkerQueue,
    callback: (scenarioWinners: ScenarioResults[]) => Promise<void>,
    startingBalanceMap: AccountSymbolBalanceMap /** @Sinan@Michelle there was a circular dependency issue, so I moved it here*/,
    onSuperseded?: () => void,
  ) {
    const runToken = ++this.runToken;
    const isSuperseded = () => runToken !== this.runToken;
    // Called even if the older run already finished: its caller ignores a run that's over.
    const supersededRun = this.onCurrentRunSuperseded;
    this.onCurrentRunSuperseded = onSuperseded;
    supersededRun?.();
    this.webWorkerQueue = webWorkerQueue;
    this.callback = callback;
    try {
      await this.runScenarios(scenarioGroup, scenarios, startingBalanceMap, isSuperseded);
    } catch (error) {
      // A failed run is over too: don't keep its caller's closure for the next run to call.
      if (!isSuperseded()) {
        this.onCurrentRunSuperseded = undefined;
      }
      throw error;
    }
  }

  private async runScenarios(
    scenarioGroup: ScenarioGroupView,
    scenarios: ScenarioView[],
    startingBalanceMap: AccountSymbolBalanceMap,
    isSuperseded: () => boolean,
  ): Promise<void> {
    const startingBalances = BalanceCalculatedUtils.mapToRecord(startingBalanceMap);

    await this.resetScenarios();

    let scenarioIndex = 0;
    this.scenarios = scenarios;
    for (const scenario of scenarios) {
      if (isSuperseded()) {
        return;
      }
      if (new Date(scenarioGroup.endDate.date) > new Date(scenarioGroup.anchorPoint.date)) {
        const scenarioOption = await this.setupScenarios(scenario, scenarioGroup, startingBalances);
        if (isSuperseded()) {
          return;
        }
        this.scenarioOptions.push(scenarioOption);
        this.totalPermutations += scenarioOption.permutations.length;
      } else {
        // if the endDate filter is before the anchor point, then we don't need to run the scenario
        await this.completeScenario(scenario, scenarioIndex, scenarioGroup, {
          createdRecords: {},
          fullTransactionList: [],
          finalBalanceAmount: null,
        });
      }
      scenarioIndex++;
    }

    if (new Date(scenarioGroup.endDate.date) > new Date(scenarioGroup.anchorPoint.date)) {
      scenarioIndex = 0;
      let stepIndex = 0;
      for (const scenario of this.scenarios) {
        if (isSuperseded()) {
          return;
        }
        if (new Date(scenarioGroup.endDate.date) > new Date(scenarioGroup.anchorPoint.date)) {
          const scenarioOption = this.scenarioOptions[scenarioIndex];

          /** This will add a mapping which Scenario is running on the THREAD */
          stepIndex += this.scenarioOptions[scenarioIndex].permutations.length;
          this.scenarioStepIndex.push({
            name: scenarioOption.constructor.name,
            permutationLength: scenarioOption.permutations.length,
            stepIndex: stepIndex,
            scenarioIndex: scenarioIndex,
          });

          // scenarioIndex is reassigned by the loop, so the completion closes over this pass's value.
          const index = scenarioIndex;
          await scenarioOption.run(
            (winner) =>
              isSuperseded()
                ? undefined
                : this.completeScenario(scenario, index, scenarioGroup, winner),
            () => {
              if (!isSuperseded()) {
                this.completePermutation();
              }
            },
          );
        }
        scenarioIndex++;
      }
    }
  }

  /** Creates and prepares a scenario's option. Doesn't touch the shared run state: runScenarioGroup()
   * adds the option and its permutations once it knows this run hasn't been superseded meanwhile. */
  async setupScenarios(
    scenario: ScenarioView,
    scenarioGroup: ScenarioGroupView,
    startingBalance: BalanceByAccountsSymbols,
  ): Promise<ScenarioOption> {
    const scenarioOption = ScenarioOptionsFactory.createScenarioOption(
      scenario.scenarioType,
      scenarioGroup,
      this.injector,
      this.webWorkerQueue,
      startingBalance,
    );

    if (new Date(scenarioGroup.endDate.date) > new Date(scenarioGroup.anchorPoint.date)) {
      await scenarioOption.beforeRunningPermutationsHook();
    }
    return scenarioOption;
  }

  async resetScenarios() {
    this.scenarioWinners = [];
    this.scenarios = [];
    this.scenarioOptions = [];
    this.scenariosCompleted = 0;
    this.totalPermutations = 0;
    this.completedPermutations = 0;
    this.scenarioStepIndex = [];
  }

  async completeScenario(
    scenario: ScenarioView,
    scenarioIndex: number,
    scenarioGroup: ScenarioGroupView,
    scenarioWinner: ScenarioOptionWinner,
  ) {
    this.scenariosCompleted++;

    this.scenarioWinners[scenarioIndex] = {
      ScenarioGroupId: scenarioGroup.id,
      scenarioID: scenario.id,
      scenarioType: scenario.scenarioType,
      scenarioName: scenario.name,
      createdRecords: scenarioWinner.createdRecords,
      fullTransactionList: scenarioWinner.fullTransactionList,
      finalBalanceAmount: scenarioWinner.finalBalanceAmount,
      helpInfo: scenarioWinner.helpInfo,
    };
    if (this.scenariosCompleted === this.scenarios.length) {
      // This run is over (completeScenario only runs for the current one): a newer run mustn't
      // report it superseded, and the singleton mustn't hold its caller's closure.
      this.onCurrentRunSuperseded = undefined;
      await this.callback(this.scenarioWinners);
    }
  }

  completePermutation() {
    this.completedPermutations++;
    const percentage = Math.round(
      this.totalPermutations === 0
        ? 0
        : (this.completedPermutations / this.totalPermutations) * 100,
    );
    this.mainProgressBar.setLoadingPercentage(percentage);

    const translatedScenarioStepIndex = this.translateScenarioStep();
    const action = `${translatedScenarioStepIndex.map((item) => item.translatedName).join(", ")}`;

    this.mainProgressBar.setProgressLabel(action);
    this.mainProgressBar.setScenarioIndex(translatedScenarioStepIndex[0]?.scenarioIndex || 0);
  }

  getScenarioStepIndex() {
    return this.scenarioStepIndex;
  }

  translateScenarioStep() {
    const scenarioStepIndex = this.getScenarioStepIndex();
    let currentStepGroupFound = false;
    // todo: the comparison is called everytime the calculation runs, potential refactor the logic
    return scenarioStepIndex
      .map((item) => {
        const translationKey = `calculating${item.name}`;
        const translatedName = this.i18nService.t(translationKey);
        const isCurrentPermutationStep =
          !currentStepGroupFound && item.stepIndex >= this.completedPermutations;
        if (isCurrentPermutationStep) {
          currentStepGroupFound = true;
        }
        return {
          ...item,
          translatedName: translatedName,
          isCurrentStep: isCurrentPermutationStep,
        };
      })
      .filter((item) => item.isCurrentStep);
  }
}
